import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Checkbox, TextField } from '@framework-controls/core';
import { JSXIf } from '@framework-controls/jsxconditions';
import { IView } from 'Framework';
import { IconNoImage } from 'src/content/icons';
import { strings } from 'src/localization';
import * as css from '../DesktopPane.module.scss';
import { AddVehicleProperties, NewVehicle, RDWMakes, RDWModels, RDWYears, SpecData, SpgOption, SpgEngine, SpgModel, ascCtaInteractionInput, ascFormEngagementInput, ascPageviewInput, ascElementConfigurationInput } from 'src/models';
import { EmptyVehicle } from 'src/models/NewVehicle/EmptyVehicle';
import { SPG_ALL_ID } from 'src/constants/Sentinels';
import { useNoVinSpgFlow } from 'src/hooks/useNoVinSpgFlow';
import { ComponentMapIds } from 'src/components/map';
import ComponentList from '../../PropertyDropDown/ComponentList/ComponentList';
import { VINEntryFlag } from 'src/enums';
import { ascCtaInteraction, ascFormEngagement, ascPageview, getElementColor, ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

// RDW only has Mercedes-Benz as a make; Sprinter variants are listed as models under it.
// IGNITE assigns Sprinter its own make code (ZN). Mercedes-Benz tile always hides Sprinter
// models. Sprinter tile (when Sprinter is in validMakes) reuses Mercedes-Benz's RDW makeId
// to fetch models, then keeps only Sprinter models. Mirrored in MobileView.tsx — keep in sync.
const MERCEDES_BENZ_MAKE_NAME = 'MERCEDES-BENZ';
const SPRINTER_MAKE_NAME = 'SPRINTER';
// Matches every Sprinter family model name: "Sprinter Cargo Van", "Sprinter Cab Chassis",
// and the electric variant "ESprinter Cargo Van" / "eSprinter Cargo Van" etc.
const SPRINTER_MODEL_REGEX = /^E?SPRINTER/i;

export interface IPropertyList {
    name: string,
    id?: string,
    image?: any,
}

interface IProps {
    vehicleSpecs?: SpecData;
    optionsSelected?: NewVehicle;
    rdwYears?: RDWYears[];
    rdwMakes?: RDWMakes[];
    rdwModels?: RDWModels[];
    theme: string;
    currentProperty: AddVehicleProperties;
    addlTrackYear: string;
    addlTrackMake: string;
    addlTrackModel: string;
    Itype: string | undefined;
    mainPaneViewOrder: IView[];
    makesBuilt: boolean;
    yearsBuilt: boolean;
    modelsBuilt: boolean;
    onAddNewVehicle: (state: NewVehicle, nextProperty?: AddVehicleProperties) => void;
    pushToLeftPane: (inputView: IView) => void;
    getMakes?: (year: string) => void;
    getModels?: (year: string, makeId: string) => void;
    vinDecode?: (vin: string) => void;
    getStyles?: (year: string, make: string, model: string) => void;
    getYears?: () => void;
    setAddVehicle: (inp: boolean) => void;
    spgOptions?: SpgOption[];
    spgEngines?: SpgEngine[];
    optionsBuilt?: boolean;
    enginesBuilt?: boolean;
    optionsKey?: string;
    enginesKey?: string;
    getSpgOptions?: (make: string, model: string, year: string, spgModelId: string) => void;
    getSpgEngines?: (make: string, model: string, year: string, spgModelId: string, optionId: string) => void;
    clearSpgVehicle?: () => void;
    spgModels?: SpgModel[];
    spgModelsBuilt?: boolean;
    spgModelsKey?: string;
    getSpgModels?: (make: string, model: string, year: string) => void;
    defaultLen: number;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    colorSensitiveEnabled: boolean,
}

const PropertyOptions = (props: IProps) => {
    const { vehicleSpecs, optionsSelected, onAddNewVehicle } = props;
    const vehicleBeingAdded = optionsSelected ?? EmptyVehicle;
    const { make, year, model, estMileage, makeId } = vehicleBeingAdded;
    const newVINentryFlag = vehicleSpecs!.newVINentryFlag;
    const [localEstMil, localEstMilChanged] = useState<string | number>(estMileage);
    const [addlTrackMakeProcessed, setAddlTrackMakeProcessed] = useState(false);
    const [addlTrackYearProcessed, setAddlTrackYearProcessed] = useState(false);
    const [addlTrackModelProcessed, setAddlTrackModelProcessed] = useState(false);
    const [mileageRaw, setMileageRaw] = useState('');
    const enterHeldRef = useRef(false);

    let showMakePane = false;
    switch (newVINentryFlag) {
        case VINEntryFlag.Show:
            showMakePane = props.currentProperty === 'make';
            break;
        case VINEntryFlag.Require:
            break;
        case VINEntryFlag.Hide:
        default:
            showMakePane = props.currentProperty === 'make';
            break;
    }

    const showYearPane = !!make && props.currentProperty === 'year';
    const showModelPane = !!year && props.currentProperty === 'model';
    const {
        isNoVinSpgFlowEnabled, showSpgModelPane, showOptionPane, showEnginePane,
    } = useNoVinSpgFlow({
        vehicleSpecs: props.vehicleSpecs,
        vehicleBeingAdded,
        onAddNewVehicle,
        spgModels: props.spgModels,
        spgModelsBuilt: props.spgModelsBuilt,
        spgModelsKey: props.spgModelsKey,
        getSpgModels: props.getSpgModels,
        spgOptions: props.spgOptions,
        optionsBuilt: props.optionsBuilt,
        optionsKey: props.optionsKey,
        getSpgOptions: props.getSpgOptions,
        spgEngines: props.spgEngines,
        enginesBuilt: props.enginesBuilt,
        enginesKey: props.enginesKey,
        getSpgEngines: props.getSpgEngines,
        clearSpgVehicle: props.clearSpgVehicle,
        currentProperty: props.currentProperty,
    });
    // Skip to the estimated mileage pane if either no models or styles exist
    const showEstMileagePane = (props.currentProperty === 'color') ||
    ((!props.rdwModels || props.rdwModels.length === 0) && !!year && props.currentProperty === 'model');
    const currentMainPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId;
    let pane = '';
    let width: string | undefined = undefined;
    let height: string | undefined = undefined;
    let currentProperty = 'make';

    const propertyList: IPropertyList[] = [];
    let otherList: string[] = [];

    useEffect(() => {
        if (props.getMakes && (!props.rdwMakes || props.rdwMakes.length === 0) && showMakePane) {
            props.getMakes('');
        }
    }, [props.rdwMakes, props.getMakes, showMakePane]);

    // Validate the Ford or Kia make if one was passed in
    useEffect(() => {
        if (
            props.addlTrackMake &&
            !addlTrackMakeProcessed &&
            props.rdwMakes &&
            props.rdwMakes.length > 0
        ) {
            let useAddlTrackMakeId: string | undefined = undefined;
            let useAddlTrackMake: string | undefined = undefined;
            if (props.vehicleSpecs?.validMakes && props.vehicleSpecs.validMakes.length > 0) {
                let makeIndex = props.vehicleSpecs.validMakes.findIndex(result => result.toUpperCase() ===
                    props.addlTrackMake.toUpperCase());
                if (makeIndex === -1) {
                    if (props.vehicleSpecs.otherMakesDesc && props.vehicleSpecs.otherMakesDesc.length > 0) {
                        makeIndex = props.vehicleSpecs.otherMakesDesc.findIndex(result => result.toUpperCase() ===
                            props.addlTrackMake.toUpperCase());
                        if (makeIndex !== -1) {
                            useAddlTrackMakeId =
                                props.rdwMakes.find(make => make.makeName.text === props.addlTrackMake)?.makeId.text;
                            useAddlTrackMake =
                                props.rdwMakes.find(make => make.makeName.text === props.addlTrackMake)?.makeName.text;
                        }
                    }
                } else {
                    useAddlTrackMakeId = props.rdwMakes.find(make => make.makeName.text === props.addlTrackMake)?.makeId.text;
                    useAddlTrackMake = props.rdwMakes.find(make => make.makeName.text === props.addlTrackMake)?.makeName.text;
                }
            }
            if (useAddlTrackMakeId && useAddlTrackMake) {
                const options = { ...optionsSelected! };
                const nextProperty: AddVehicleProperties = 'year';
                if (props.getYears) {
                    props.getYears();
                }
                options['color'] = 'default';
                options['make'] = useAddlTrackMake;
                options['makeId'] = useAddlTrackMakeId;
                onAddNewVehicle({ ...options }, nextProperty);
                props.pushToLeftPane({
                    viewId: ComponentMapIds.PropertyOptions,
                    component: ComponentMapIds.PropertyOptions,
                });
            }
            setAddlTrackMakeProcessed(true);
        }
    }, [
        addlTrackMakeProcessed,
        onAddNewVehicle,
        optionsSelected,
        props.addlTrackMake,
        props.pushToLeftPane,
        props.getYears,
        props.vehicleSpecs?.validMakes,
        props.vehicleSpecs?.otherMakesDesc,
        props.rdwMakes,
    ]);

    // Validate the Ford or Kia year if one was passed in
    useEffect(() => {
        if (
            make &&
            props.addlTrackYear &&
            props.rdwYears &&
            props.rdwYears.length > 0 &&
            addlTrackMakeProcessed &&
            !addlTrackYearProcessed
        ) {
            const options = { ...optionsSelected! };
            const addlYear = props.rdwYears.find(year => year.year.text === props.addlTrackYear)?.year.text;
            if (addlYear) {
                const nextProperty: AddVehicleProperties = 'model';
                options['year'] = props.addlTrackYear;
                onAddNewVehicle({ ...options }, nextProperty);
                props.pushToLeftPane({
                    viewId: ComponentMapIds.PropertyOptions,
                    component: ComponentMapIds.PropertyOptions,
                });
            }
            setAddlTrackYearProcessed(true);
        }
    }, [
        addlTrackMakeProcessed,
        addlTrackYearProcessed,
        onAddNewVehicle,
        optionsSelected,
        props.addlTrackYear,
        props.pushToLeftPane,
        props.getModels,
        makeId,
        make,
        props.rdwYears,
    ]);

    // Validate the Ford or Kia model if one was passed in
    useEffect(() => {
        if (
            year &&
            props.addlTrackModel &&
            props.rdwModels &&
            props.rdwModels.length > 0 &&
            addlTrackYearProcessed &&
            !addlTrackModelProcessed
        ) {
            const options = { ...optionsSelected! };
            const addlModelId = props.rdwModels.find(model => model.modelName.text === props.addlTrackModel)?.modelId.text;
            if (addlModelId) {
                const nextProperty: AddVehicleProperties = 'color';
                options['model'] = props.addlTrackModel;
                options['modelId'] = addlModelId;
                onAddNewVehicle({ ...options }, nextProperty);
                props.pushToLeftPane({
                    viewId: ComponentMapIds.PropertyOptions,
                    component: ComponentMapIds.PropertyOptions,
                });
            }
            setAddlTrackModelProcessed(true);
        }
    }, [
        addlTrackYearProcessed,
        addlTrackModelProcessed,
        onAddNewVehicle,
        optionsSelected,
        props.addlTrackModel,
        props.pushToLeftPane,
        props.getModels,
        props.rdwModels,
        year,
    ]);

    if (showMakePane) {
        pane = 'Makes';
        currentProperty = 'make';
        if (props.vehicleSpecs?.validMakes && props.vehicleSpecs.validMakes.length > 0) {
            props.vehicleSpecs.validMakes.forEach((make) => {
                if (props.rdwMakes && props.rdwMakes.length > 0) {
                    const makeName = props.rdwMakes?.find((a) => a.makeName.text.toUpperCase() === make.toUpperCase());
                    const pList: IPropertyList = { name: '', image: null, id: '' };
                    pList.name = makeName !== undefined ? makeName.makeName.text : make;
                    pList.id = makeName !== undefined ? makeName.makeId.text : '';
                    pList.image = <IconNoImage />;
                    propertyList.push(pList);
                }
            });
            if (props.vehicleSpecs.otherMakes &&
                props.vehicleSpecs.otherMakesDesc &&
                props.vehicleSpecs.otherMakesDesc.length > 0) {
                const pList: IPropertyList = { name: '', image: null };
                pList.name = 'Other Make';
                pList.image = null;
                propertyList.push(pList);
                otherList = vehicleSpecs?.otherMakesDesc ? vehicleSpecs.otherMakesDesc.slice() : [];
                if (props.vehicleSpecs.specsOtherMakesIncFlag !== '+' && !otherList.includes('Scion') &&
                    !props.vehicleSpecs.validMakes.includes('Scion')) {
                    otherList.push('Scion');
                    otherList.sort();
                }
                if (props.vehicleSpecs.specsOtherMakesIncFlag === '-') {
                    if (props.vehicleSpecs.specsOtherMakesFieldList &&
                        props.vehicleSpecs.specsOtherMakesFieldList.length > 0 &&
                        props.vehicleSpecs.specsOtherMakesFieldList.includes('2I')) {
                        const scionIndex = otherList.indexOf('Scion');
                        if (scionIndex !== -1) {
                            otherList.splice(scionIndex, 1);
                        }
                    }
                }
            }
        } else if (props.rdwMakes && props.rdwMakes.length > 0) {
            if (props.vehicleSpecs) {
                if (props.vehicleSpecs.otherMakes &&
                    props.vehicleSpecs.otherMakesDesc &&
                    props.vehicleSpecs.otherMakesDesc.length > 0) {
                    const pList: IPropertyList = { name: '', image: null };
                    pList.name = 'Other Make';
                    pList.image = null;
                    propertyList.push(pList);
                    otherList = vehicleSpecs?.otherMakesDesc ? vehicleSpecs.otherMakesDesc : [];
                }
            }
        }
    }

    if (showYearPane) {
        pane = 'Years';
        currentProperty = 'year';
        let yearCount = 0;
        if (props.rdwYears && props.rdwYears.length > 0) {
            props.rdwYears?.forEach((year) => {
                yearCount++;
                const pList: IPropertyList = { name: '', image: null };
                if (yearCount > 8) {
                    if (yearCount === 9) {
                        pList.name = 'Other Year';
                        pList.image = null;
                        propertyList.push(pList);
                    }
                    otherList.push(year.year.text);
                } else {
                    pList.name = year.year.text;
                    pList.image = null;
                    propertyList.push(pList);
                }
            });
        }
        width = '8rem';
        height = '2.4rem';
    }

    if (showModelPane) {
        pane = 'Models';
        currentProperty = 'model';
        // When there is only one model, we dont get an array back
        // so if rdwModels exists but we have no length, we are getting a single object
        // as such we have one model and we need to add it to the list
        if (props.rdwModels && props.rdwModels.length === undefined) {
            const test: RDWModels[]= Object.values(props.rdwModels);
            const pList: IPropertyList = { name: '', image: null, id: '' };
            pList.name = Object.values(test[1]).toString();
            pList.id = Object.values(test[0]).toString();
            pList.image = <IconNoImage />;
            propertyList.push(pList);
        }
        if (props.rdwModels && props.rdwModels.length > 0) {
            const upperMake = make.toUpperCase();
            const isMercedesBenz = upperMake === MERCEDES_BENZ_MAKE_NAME;
            const isSprinter = upperMake === SPRINTER_MAKE_NAME;
            props.rdwModels.forEach((model) => {
                const modelName = model.modelName.text;
                const isSprinterModel = SPRINTER_MODEL_REGEX.test(modelName);
                if (isMercedesBenz && isSprinterModel) {
                    return;
                }
                if (isSprinter && !isSprinterModel) {
                    return;
                }
                const pList: IPropertyList = { name: '', image: null, id: '' };
                pList.name = modelName;
                pList.id = model.modelId.text;
                pList.image = <IconNoImage />;
                propertyList.push(pList);
            });
        }
    }
    if (showSpgModelPane) {
        pane = 'SpgModel';
        currentProperty = 'spgModel';
        (props.spgModels ?? []).forEach((m) => {
            if (m.desc && m.desc.trim() !== '') {
                propertyList.push({ name: m.desc, id: m.id, image: null });
            }
        });
    }
    if (showOptionPane) {
        pane = 'Options';
        currentProperty = 'option';
        // Skip picking a specific option — engines are then fetched
        propertyList.push({ name: strings.AllSpgOptions, id: SPG_ALL_ID, image: null });
        (props.spgOptions ?? []).forEach((opt) => {
            if (opt.desc && opt.desc.trim() !== '') {
                propertyList.push({ name: opt.desc, id: opt.id, image: null });
            }
        });
    }
    if (showEnginePane) {
        pane = 'Engine';
        currentProperty = 'engine';
        // Skip picking a specific engine match every possible engine.
        propertyList.push({ name: strings.AllSpgEngines, id: SPG_ALL_ID, image: null });
        (props.spgEngines ?? []).forEach((eng) => {
            if (eng.name && eng.name.trim() !== '') {
                propertyList.push({ name: eng.name, id: eng.id, image: null });
            }
        });
    }
    if (showEstMileagePane) {
        pane = 'EstMileage';
        currentProperty = 'estMileage';
    }

    let cbkClassName = 'primaryCkb';
    if (pane === 'EstMileage' && localEstMil === strings.Unknown) {
        cbkClassName = 'primaryCkbChecked';
    }

    useEffect(() => {
        // Perform this getModels even with no makeId to ensure that the models are cleared if make
        // selection has no id attached to it.
        let varMakeId = makeId;
        const varMake = make.toLowerCase();
        if (varMake === 'ford truck') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Ford');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'general motors') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'GMC');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'ford canada') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Ford');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'chrysler canada') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Chrysler');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'vw canada') {
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Volkswagen');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (varMake === 'sprinter') {
            // Sprinter has no entry in RDW makes; reuse Mercedes-Benz's makeId so the
            // model fetch returns Mercedes-Benz models. The render-time filter keeps only
            // models whose name starts with "Sprinter".
            if (props.rdwMakes) {
                const varRDWMakes = props.rdwMakes?.find((a) => a.makeName.text === 'Mercedes-Benz');
                if (varRDWMakes) {
                    varMakeId = varRDWMakes?.makeId.text;
                }
            }
        }
        if (year !== '' && model === '') {
            if (props.getModels) {
                props.getModels(year, varMakeId);
            }
        }
    }, [year, makeId, props.getModels, model, make, props.rdwMakes]);


    useEffect(() => {
        if (year !== '' && make !== '' && model !== '') {
            if (props.getStyles) {
                props.getStyles(year, make, model);
            }
        }
    }, [year, make, model, props.getStyles]);

    let selectHeader = '';
    let selectHeaderCss = classNames(
        css.nameSelector,
        {
            [css.nameSelectorHonda]: props.theme === 'honda',
            [css.nameSelectorIN]: props.theme === 'infiniti',
            [css.nameSelectorMazda]: props.theme === 'mazda',
            [css.nameSelectorAcura]: props.theme === 'acura',
            [css.makeSelectorGenesis]: props.theme === 'genesis',
            [css.nameSelectorLexus]: props.theme === 'lexus',
        },
    );

    const standardHeaderStyles = classNames(
        css.standardHeader,
        {
            [css.standardHeaderFord]: props.theme === 'ford',
        },
    );
    switch (pane) {
        case 'Makes':
            selectHeader = strings.SelectMake;
            break;
        case 'Years':
            selectHeader = strings.SelectYear;
            break;
        case 'Models':
            selectHeader = strings.SelectModel;
            break;
        case 'SpgModel':
            selectHeader = strings.SelectSpgModel;
            break;
        case 'Options':
            selectHeader = strings.SelectOption;
            break;
        case 'Engine':
            selectHeader = strings.SelectEngine;
            break;
        case 'EstMileage':
            selectHeader = strings.SelectColorMileage;
            selectHeaderCss = standardHeaderStyles;
            break;
        default:
            break;
    }

    useEffect(() => {
        // Creating a GA4 trigger when moving to the estimated mileage panel.
        if (props.currentProperty === 'color' &&
        props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.AddVehicle) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'itemlist',
                errorCode: '',
                itemResults: '',
            };
            const newVehicle = {
                id: '',
                vin: optionsSelected?.vin ?? '',
                makeCode: optionsSelected?.makeCode ?? '',
                make: optionsSelected?.make ?? '',
                model: optionsSelected?.model ?? '',
                imagePath: '',
                year: optionsSelected?.year ?? '',
                color: optionsSelected?.color ?? '',
                style: optionsSelected?.trim ?? '',
                prevMileage: 0,
                prevRoDate: '',
                nextAppt: '',
                lastAdv: '',
                lastAdvName: '',
                lastAdvImage: '',
                lastTrans: '',
                lastTransLit: '',
                nameId: '',
            };
            ascPageview(ascPageViewInp, [newVehicle]);
        }
    }, [props.currentProperty,
        optionsSelected?.vin,
        optionsSelected?.makeCode,
        optionsSelected?.make,
        optionsSelected?.model,
        optionsSelected?.year,
        optionsSelected?.color,
        optionsSelected?.trim,
        props.mainPaneViewOrder,
    ]);

    const estMileageChangedHandler = useCallback((_id: string, value: string | number, _isError: boolean) => {
        const strValue = value.toString();
        if (strValue.length <= 7) {
            const numericString = strValue?.replace(/,/g, '');
            let newMileage = parseFloat(numericString);
            if (isNaN(newMileage)) {
                newMileage = 0;
            }
            setMileageRaw(numericString);
            localEstMilChanged(value);
            const formattedMileage = numericString ? parseFloat(numericString).toLocaleString('en-US') : '';
            const options = { ...optionsSelected! };
            options['estMileage'] = !newMileage ? '' : formattedMileage;
            onAddNewVehicle({ ...options });
        }
    }, [optionsSelected, onAddNewVehicle, localEstMilChanged]);

    const onCheckedHandler = useCallback((e: any) => {
        if (localEstMil !== strings.Unknown) {
            localEstMilChanged(strings.Unknown);
            setMileageRaw('');
            const options = { ...optionsSelected! };
            options['estMileage'] = strings.Unknown;
            onAddNewVehicle({ ...options });
        } else {
            localEstMilChanged('');
            const options = { ...optionsSelected! };
            options['estMileage'] = '';
            onAddNewVehicle({ ...options });
        }
        const newVehicle = {
            id: '',
            vin: optionsSelected?.vin ?? '',
            makeCode: optionsSelected?.makeCode ?? '',
            make: optionsSelected?.make ?? '',
            model: optionsSelected?.model ?? '',
            imagePath: '',
            year: optionsSelected?.year ?? '',
            color: optionsSelected?.year ?? '',
            style: optionsSelected?.trim ?? '',
            prevMileage: 0,
            prevRoDate: '',
            nextAppt: '',
            lastAdv: '',
            lastAdvName: '',
            lastAdvImage: '',
            lastTrans: '',
            lastTransLit: '',
            nameId: '',
        };
        const elementColor = getElementColor(e, 'fill');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'itemlist',
            elementText: strings.UnknownMileage,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, [newVehicle]);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.UnknownMileage,
            elementType: 'item_details',
            elementColor: elementColor,
            eventAction: 'click',
            eventActionResult: 'check',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [localEstMil, optionsSelected, onAddNewVehicle]);

    const accordionHandler = useCallback((target: any, id?: string, color?: boolean) => {
        const options = { ...optionsSelected! };
        let property = currentProperty;
        let makeId = id;
        if (property === 'make') {
            if (!id && props.rdwMakes) {
                makeId = props.rdwMakes.find(a => {
                    // In STDMakes the code JP is listed as JAG but in RDWMakes the make is spelled out as Jaguar.
                    if (target === 'JAG') {
                        return a.makeName.text.toUpperCase() === 'JAGUAR';
                    }

                    return a.makeName.text === target;
                })?.makeId.text;
            }
        }
        // Need to set property to color if the color field was changed
        if (color) {
            property = 'color';
        }
        if (typeof (target) === 'string') {
            options[property] = target;
        } else {
            options[property] = target.id;
        }

        let nextProperty: AddVehicleProperties = 'year';
        switch (property) {
            case 'make':
            default:
                if (props.getYears) {
                    props.getYears();
                }
                options['year'] = '';
                options['model'] = '';
                options['estMileage'] = '';
                options['color'] = 'default';
                options['makeId'] = makeId ? makeId : '';
                options['modelId'] = '';
                // Manually picking a make means the user has moved on from any earlier VINOWNED
                // rejection — don't let a stale vinOwned flag keep skipping the SPG panes.
                options['vinOwned'] = false;
                break;
            case 'year':
                options['model'] = '';
                options['estMileage'] = '';
                options['color'] = 'default';
                options['modelId'] = '';
                // Manually picking a year means the user has moved on from any earlier VINOWNED
                // rejection — don't let a stale vinOwned flag keep skipping the SPG panes.
                options['vinOwned'] = false;
                nextProperty = 'model';
                break;
            case 'model':
                options['estMileage'] = '';
                options['color'] = 'default';
                options['modelId'] = id ? id : '';
                options['spgModel'] = '';
                options['spgModelId'] = '';
                options['option'] = '';
                options['optionId'] = '';
                options['engine'] = '';
                options['engineId'] = '';
                // Manually picking a model means the user has moved on from any earlier VINOWNED
                // rejection — don't let a stale vinOwned flag keep skipping the SPG panes.
                options['vinOwned'] = false;
                // Only enter the SPG Model/Option/Engine chain when the no-VIN SPG pricing
                // flow is enabled, otherwise existing behavior is preserved.
                nextProperty = isNoVinSpgFlowEnabled ? 'spgModel' : 'color';
                break;
            case 'spgModel':
                options['spgModel'] = typeof target === 'string' ? target : '';
                options['spgModelId'] = id ? id : '';
                options['option'] = '';
                options['optionId'] = '';
                options['engine'] = '';
                options['engineId'] = '';
                nextProperty = 'option';
                break;
            case 'option':
                options['option'] = typeof target === 'string' ? target : '';
                options['optionId'] = id ? id : '';
                options['engine'] = '';
                options['engineId'] = '';
                nextProperty = 'engine';
                break;
            case 'engine':
                options['engine'] = typeof target === 'string' ? target : '';
                options['engineId'] = id ? id : '';
                nextProperty = 'color';
                break;
            case 'color':
            case 'estMileage':
                nextProperty = 'color';
                break;
        }
        onAddNewVehicle({ ...options }, nextProperty);
        const newVehicle = {
            id: '',
            vin: optionsSelected?.vin ?? '',
            makeCode: optionsSelected?.makeCode ?? '',
            make: optionsSelected?.make ?? '',
            model: optionsSelected?.model ?? '',
            imagePath: '',
            year: optionsSelected?.year ?? '',
            color: optionsSelected?.year ?? '',
            style: optionsSelected?.trim ?? '',
            prevMileage: 0,
            prevRoDate: '',
            nextAppt: '',
            lastAdv: '',
            lastAdvName: '',
            lastAdvImage: '',
            lastTrans: '',
            lastTransLit: '',
            nameId: '',
        };
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'itemlist',
            elementText: target.toString(),
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        const ascVehicle = {
            ...newVehicle,
            make: options.make ?? newVehicle.make,
            model: options.model ?? newVehicle.model,
            year: options.year ?? newVehicle.year,
            style: options.trim ?? newVehicle.style,
        };
        ascCtaInteraction(ascCtaInteractionInp, target, [{...ascVehicle}]);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: target.toString(),
            elementType: 'item_details',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, target);
        props.pushToLeftPane({
            viewId: ComponentMapIds.PropertyOptions,
            component: ComponentMapIds.PropertyOptions,
        });
    }, [optionsSelected, onAddNewVehicle, props.rdwMakes, props?.pushToLeftPane, props?.getYears,
        currentProperty, isNoVinSpgFlowEnabled]);

    const onBlurHandler = (e: any) => {
        const newVehicle = {
            id: '',
            vin: optionsSelected?.vin ?? '',
            makeCode: optionsSelected?.makeCode ?? '',
            make: optionsSelected?.make ?? '',
            model: optionsSelected?.model ?? '',
            imagePath: '',
            year: optionsSelected?.year ?? '',
            color: optionsSelected?.year ?? '',
            style: optionsSelected?.trim ?? '',
            prevMileage: 0,
            prevRoDate: '',
            nextAppt: '',
            lastAdv: '',
            lastAdvName: '',
            lastAdvImage: '',
            lastTrans: '',
            lastTransLit: '',
            nameId: '',
        };
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'itemlist',
            elementText: strings.EnterEstimatedMileage,
            elementColor: '',
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, [newVehicle]);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.EnterEstimatedMileage,
            elementType: 'item_details',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'itemlist',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.EnterEstimatedMileage,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, [newVehicle]);
    };

    const onKeyPressHandler = (key: string) => {
        if (key === 'Enter' && !enterHeldRef.current) {
            enterHeldRef.current = true;
            if (estMileage === strings.Unknown || Number(mileageRaw) > 0) {
                props.setAddVehicle(true);
            }
        }
    };

    const onKeyUpWrapper = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter') {
            enterHeldRef.current = false;
        }
    };

    const labelClassMap = {
        bmw: css.mileageCheckboxBMW,
    };

    const labelClass = labelClassMap[props.theme] || css.mileageCheckBox;

    return (
        <div className={selectHeaderCss}>
            {selectHeader}
            <JSXIf Condition={pane !== 'EstMileage'}>
                <div className={css.componentListContainer} id='componentListContainer'>
                    <ComponentList
                        list={propertyList}
                        click={accordionHandler}
                        width={width}
                        height={height}
                        selectedOptions={optionsSelected!}
                        pane={pane}
                        otherList={otherList}
                        theme={props.theme}
                        defaultLen={props.defaultLen}
                        Itype={props?.Itype}
                        makesBuilt={props.makesBuilt}
                        yearsBuilt={props.yearsBuilt}
                        modelsBuilt={props.modelsBuilt}
                        currentMainPane={currentMainPane}
                        vehicleSpecs={props.vehicleSpecs}
                        sessionId = {props.sessionId}
                        lexusMCLaunched = {props.lexusMCLaunched}
                        lexusTDMCLaunched = {props.lexusTDMCLaunched}
                        toyotaMCLaunched = {props.toyotaMCLaunched}
                        toyotaTDMCLaunched = {props.toyotaTDMCLaunched}
                        colorSensitiveEnabled={props.colorSensitiveEnabled}
                    />
                </div>
            </JSXIf>
            <JSXIf Condition={pane === 'EstMileage'}>
                <span>{strings.RequiredFields}</span>
                <div className={css.estMileageInput} id='estMileageInput'>
                    <div className={css.estMileageNumberField} onKeyUp={onKeyUpWrapper}>
                        <TextField
                            type='tel'
                            id='estMileageText'
                            label={strings.EnterEstimatedMileage}
                            value={(!estMileage || estMileage === 'Unknown') ? '' :
                                (mileageRaw ? parseFloat(mileageRaw).toLocaleString('en-US') : '')}
                            onChange={estMileageChangedHandler}
                            disabled={estMileage === strings.Unknown}
                            onBlur={onBlurHandler}
                            onKeyPress={(e) => onKeyPressHandler(e.key)}
                        />
                    </div>
                    <div className={css.mileageCheckbox} onKeyPress={(e) => onKeyPressHandler(e.key)} onKeyUp={onKeyUpWrapper}>
                        <Checkbox
                            id='estMilCheckbox'
                            checked={estMileage === strings.Unknown}
                            size='rg'
                            variant={cbkClassName}
                            labelClass={labelClass}
                            label={strings.UnknownMileage}
                            labelPlacement='end'
                            onChange={(e) => onCheckedHandler!(e)}
                        />
                    </div>
                </div>
            </JSXIf>
        </div>
    );
};

export default PropertyOptions;